import { Icon, RadioGroup } from '@arcadiapower/shrike';
import { useEffect, useState } from 'react';
import { HelpDialog } from '~/components/dialogs';
import { Disclaimer } from '~/components/disclaimer';
import { HelpText } from '~/components/help-text';
import {
  type ImageType,
  TERMS_IMAGE,
  TERMS_IMAGE_WAITLIST,
  ImageSelector,
} from '~/components/image';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { ReviewBlock } from '~/components/review-block';
import { Separator } from '~/components/separator';
import { SolarTerms } from '~/components/terms/solar-terms';
import { Description, StyledUl, Text, Title, ReviewItemLogoWrapper } from '~/components/typography';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { MINNESOTA_LMI_FUNNEL_ID } from '~/funnels/minnesota-lmi/config';
import { SOLAR_LANDSCAPE_FUNNEL_ID } from '~/funnels/solar-landscape/config';
import { useCopy } from '~/hooks';
import { LDClient, LDFlags } from '~/providers';
import { SelfAttestationForm } from '~/screens/vnm-terms/self-attestation-form/self-attestation-form';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { BillingDataSource, ContractVersion, Utility } from '~/typings/context';
import { FunnelTrack } from '~/typings/graphql';
import { ARCADIA_REWARDS_URL } from '~/utils/constants';
import { getSavingsRateDescription } from '~/utils/funnels';
import { getUserIpFromStore, sendToSegment, TRACK } from '~/utils/tracking';
import { VnmTermsLink, RewardsSection } from './vnm-terms.style';

const rewardsLink = ARCADIA_REWARDS_URL;
const lmiOptions: LmiStatus[] = ['isLmi', 'maybeLmi', 'notLmi'];

export const VnmTerms = ({
  context,
  error,
  flags,
  loading,
  next,
  overrideCopy,
  pending,
}: VnmTermsProps) => {
  const copy = useCopy(context, checkCopy, overrideCopy);
  const [lmiStatus, setLmiStatus] = useState<LmiStatus | undefined>();
  const { billingSelection, contracts, paymentMethod } = context;
  const [selfAttestationData, setSelfAttestationData] = useState<SelfAttestationData>(
    {} as SelfAttestationData,
  );
  const [showSelfAttestationForm, setShowSelfAttestationForm] = useState(false);
  const showRewardsGuidelines = LDClient.variation(LDFlags.showRewardsGuidelines);

  const canSubmitHandler = () => {
    // if we are not showing the review block, we need to make sure
    // that the user can submit the terms without selecting an LMI status
    if (flags?.showReviewBlock) {
      return !!lmiStatus;
    }
    return true;
  };

  const isNM = context.utility.state.abbreviation === 'NM';
  const isIL = context.utility.state.abbreviation === 'IL';
  const isWaitlist = context.forceWaitlist || context.utility.funnelTrack === FunnelTrack.Waitlist;
  const utilityAccountMissingError =
    context.funnelPacket?.utilityAccount?.utilityAccountMissingError;
  const isMD = context.utility.state.abbreviation === 'MD';
  const isSolarLandscape = context.funnelId === SOLAR_LANDSCAPE_FUNNEL_ID;
  const isGoSolar = context.funnelId === GO_SOLAR_FUNNEL_ID;
  const isCommunitySolar = context.funnelId === COMMUNITY_SOLAR_FUNNEL_ID;
  const showLMISavingsMessage = () => {
    return isSolarLandscape || (isMD && (isGoSolar || isCommunitySolar));
  };

  const consolidateScrollableTermsText = shouldConsolidateContracts(
    context.utility.state.abbreviation,
    LDClient.variation(LDFlags.ffEnableNJAffirmativeConsent),
  );

  useEffect(() => {
    if (flags?.showSelfAttestationForm) {
      if (['isLmi', 'maybeLmi'].includes(lmiStatus!)) {
        setShowSelfAttestationForm(true);
      } else {
        setShowSelfAttestationForm(false);
      }
    }
  }, [lmiStatus]);

  return (
    <TwoColumnGrid testId="screen-VnmTerms">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={4}>{copy.description}</Description>
        <StyledUl
          mb={4}
          style={{
            marginBottom: showRewardsGuidelines ? '12px' : '16px',
          }}
        >
          {[copy.costBenefit, copy.energyBenefit, copy.savingsBenefit].map(item => (
            <li key={item}>
              <Icon
                margin="4px 8px 0 8px"
                scale={2}
                icon="Node"
                color="successBright"
                style={{ marginRight: '16px' }}
              />
              {item}
            </li>
          ))}
        </StyledUl>
        {showRewardsGuidelines &&
          billingSelection === 'acb' &&
          context.utility.funnelTrack !== FunnelTrack.Waitlist && (
            <Description>
              <RewardsSection>
                <Icon
                  margin="4px 8px 0 8px"
                  scale={2}
                  icon="Node"
                  color="successBright"
                  style={{ marginRight: '16px' }}
                />
                <span>
                  Earning points every month and redeeming them for rewards through our{' '}
                  <Link
                    id="rewardsLink"
                    openInNewTab={false}
                    onClick={() => window.open(rewardsLink, '_blank')}
                    color="primary"
                  >
                    Arcadia Rewards Program
                  </Link>
                </span>
              </RewardsSection>
            </Description>
          )}
        <Description>{copy.eligibilityNotice}</Description>
        {context.funnelId === MINNESOTA_LMI_FUNNEL_ID && (
          <Disclaimer textAlign="left">{copy.savingBenefitsDisclaimer}</Disclaimer>
        )}
        {showLMISavingsMessage() ? (
          <Text fontSize="14px">{copy.lmiSavingsPercentageDescription}</Text>
        ) : null}
        <Container mt={3}>
          <HelpText />
        </Container>
        <Separator />
        <ReviewBlock>
          <ReviewBlock.Item label="Name" notranslate>
            {`${context.firstName} ${context.lastName}`}
          </ReviewBlock.Item>
          <ReviewBlock.Item label="Email" notranslate>
            {context.email}
          </ReviewBlock.Item>
        </ReviewBlock>
        {!!(paymentMethod && billingSelection === 'dual') && (
          <>
            <Separator />
            <ReviewBlock>
              <ReviewBlock.Item label="Utility account information">
                <ReviewItemLogoWrapper>
                  <Icon
                    scale={2}
                    icon="CheckmarkCircleInverted"
                    color="successBright"
                    style={{ marginRight: '8px', marginTop: '4px' }}
                  />
                  <Text color="successBright" textStyle="paragraph500">
                    Provided manually
                  </Text>
                </ReviewItemLogoWrapper>
                <Text textStyle="paragraph400" opacity="low">
                  {copy.accountInfo}
                </Text>
              </ReviewBlock.Item>
            </ReviewBlock>
          </>
        )}
        {!!utilityAccountMissingError && isIL ? (
          <>
            <Separator />
            <ReviewBlock>
              <ReviewBlock.Item label="Utility account information">
                <ReviewItemLogoWrapper>
                  <Icon
                    scale={2}
                    icon="ExclamationCircleInverted"
                    color="errorBright"
                    style={{ marginRight: '8px', marginTop: '4px' }}
                  />
                  <Text color="error" textStyle="paragraph500">
                    We were unsuccessful in setting up your utility account.
                  </Text>
                </ReviewItemLogoWrapper>
                <HelpDialog
                  target={
                    <Text textStyle="paragraph400" opacity="low">
                      We couldn’t find your utility account details, so we can't generate your
                      contracts right now. For help, please contact our
                      <Link
                        color="primary"
                        track={TRACK.supportLinkClicked}
                        style={{
                          whiteSpace: 'nowrap',
                          display: 'inline-block',
                          paddingLeft: 5,
                        }}
                      >
                        <Text fontSize="small" textStyle="heading600">
                          support team
                        </Text>
                      </Link>{' '}
                      .You can still finish signing up by clicking 'Review and sign forms.'
                    </Text>
                  }
                />
              </ReviewBlock.Item>
            </ReviewBlock>
          </>
        ) : null}
        {!!(paymentMethod && billingSelection !== 'ucb') && (
          <>
            <Separator />
            <ReviewBlock>
              <ReviewBlock.Item label="Payment method" notice={copy.paymentNotice}>
                {paymentMethod.lastFour
                  ? `${paymentMethod.source} ending in •••• ${paymentMethod.lastFour}`
                  : `${paymentMethod.source}`}
              </ReviewBlock.Item>
            </ReviewBlock>
          </>
        )}

        <Separator />
        {/* move to radios directory */}
        {flags?.showReviewBlock && (
          <>
            <ReviewBlock>
              <ReviewBlock.Item label={copy.lmiSectionTitle} notice={copy.lmiSectionDescription} />
              <RadioGroup
                margin="24px 0 0 0"
                name="lmi-selection"
                options={(lmiOptions as LmiStatus[]).map(choice => ({
                  id: choice,
                  children: (
                    <Container key={choice} onClick={() => setLmiStatus(choice)}>
                      {copy[choice].primary}
                      <Text fontSize="small" margin="10px 0">
                        {copy[choice].secondary}
                      </Text>
                    </Container>
                  ),
                  value: choice,
                }))}
                isChecked={(_, value) => value === lmiStatus}
                onChange={(selection: LmiStatus) => setLmiStatus(selection)}
                width="initial"
              />
            </ReviewBlock>
            <Separator />
            {showSelfAttestationForm && (
              <ReviewBlock>
                <ReviewBlock.Item
                  label="Low and moderate income (LMI) criteria"
                  notice={copy.paymentNotice}
                >
                  <div style={{ marginTop: '32px' }}>
                    <SelfAttestationForm context={context} onFormUpdate={setSelfAttestationData} />
                  </div>
                </ReviewBlock.Item>
                <Separator />
              </ReviewBlock>
            )}
          </>
        )}
        {LDClient.variation(LDFlags.showLmiNoticeInLegalPage) && (
          <ReviewBlock>
            <ReviewBlock.Item notranslate>
              <Text data-testid="lmi-notice" color="secondary" textStyle="paragraph400">
                {copy.lmiNotice}
              </Text>
            </ReviewBlock.Item>
          </ReviewBlock>
        )}
        <SolarTerms
          canSubmit={canSubmitHandler()}
          consolidateScrollableTermsText={consolidateScrollableTermsText}
          contracts={isWaitlist || isNM ? [] : contracts}
          copy={copy}
          error={error}
          loading={loading}
          onSubmit={({ contractIds }) => {
            if (flags?.showSelfAttestationForm) {
              onSubmitSolarTerms({ lmiStatus, contractIds, next, context, selfAttestationData });
            }
            onSubmitSolarTerms({ lmiStatus, contractIds, next, context });
          }}
          pending={pending}
          utilityAccountId={context.utilityAccountId}
          showPersonalizedContractPreview={flags?.showPersonalizedContractPreview}
        />
        {copy.disclaimer && <Disclaimer mt={7}>{copy.disclaimer}</Disclaimer>}
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const sendToSegmentOnLmiClick = (userLmiInput: LmiStatus, context: any) => {
  /* eslint-disable camelcase */
  const user = context?.funnelPacket?.user;
  const state = context.funnelPacket?.utility.state;
  const { email, firstName: first_name, phone, lastName: last_name } = user;
  const { funnelPacket } = context;
  const promoCode = funnelPacket?.user?.promoCode;
  const userIp: any = getUserIpFromStore();
  const segmentPayload = {
    userIp,
    email,
    phone,
    first_name,
    last_name,
    promo_code: promoCode,
    state: state?.abbreviation,
    inviteCode: funnelPacket?.inviteCode,
  };
  switch (userLmiInput) {
    case isLmi:
      sendToSegment({
        type: 'user_opted_yes_for_lmi',
        funnelPacket: context.funnelPacket!,
        additional: {
          ...segmentPayload,
          event_message: 'User Selected LMI Option - Yes',
          userLmiInput: isLmi,
        },
      });
      break;
    case maybeLmi:
      sendToSegment({
        type: 'user_opted_maybe_for_lmi',
        funnelPacket: context.funnelPacket!,
        additional: {
          ...segmentPayload,
          event_message: 'User Selected LMI Option - Maybe',
          userLmiInput: maybeLmi,
        },
      });
      break;
    case notLmi:
      sendToSegment({
        type: 'user_opted_no_for_lmi',
        funnelPacket: context.funnelPacket!,
        additional: {
          ...segmentPayload,
          event_message: 'User Selected LMI Option - No',
          userLmiInput: notLmi,
        },
      });
      break;
  }
  /* eslint-enable camelcase */
};

export const onSubmitSolarTerms = ({
  context,
  next,
  lmiStatus,
  contractIds,
  selfAttestationData,
}: onSubmitSolarTermsProps) => {
  if (lmiStatus) {
    sendToSegmentOnLmiClick(lmiStatus, context);
  }

  if (selfAttestationData) {
    next({ lmiStatus, contractIds, selfAttestationData });
  }
  next({ lmiStatus, contractIds });
};

export const checkCopy = context => {
  // Ohio Edison(2349), AEP OH(2342), Toeldo Edison(2531), Eversource / PSNH(2326) = isBlockedUtility
  if (
    context?.forceWaitlist ||
    context.utility.funnelTrack === FunnelTrack.Waitlist ||
    [2349, 2342, 2531, 2326].includes(context.utility.id)
  ) {
    return context.isUtilityScraperEligible ? waitListCopy() : waitListCopyNoScraper();
  } else if (context.funnelId === MINNESOTA_LMI_FUNNEL_ID) {
    return {
      ...defaultCopy(context),
      ...mnlmiCopy(),
    };
  } else {
    return defaultCopy(context);
  }
};

export const defaultCopy = (context: VnmTermsContext) => {
  const utilityName = context.utility.displayName;
  const state = context.utility.state.abbreviation;
  const hasContracts = !!context.contracts.length;
  const isWaitlist = context.forceWaitlist || context.utility.funnelTrack === FunnelTrack.Waitlist;
  const isNonABP = context.funnelId === 'nonABP';
  const isEnrolledNM = state === 'NM' && !isWaitlist;
  const isEnrolledIL = state === 'IL' && !isWaitlist && !isNonABP;

  const savingsRate =
    context.solarSavingsRate ??
    getSavingsRateDescription(state, context.funnelId, context.utility.id);

  const paymentNotice =
    context.billingSelection === 'dual'
      ? "You'll start seeing statements from us once we assign you to an active solar farm. Expect net savings from the credits you earn, minus your subscription fee."
      : `You will not be charged today. When your next bill from ${utilityName} is ready, we'll send you the details and charge your payment method on your account three days later.`;

  const lmiNotice = (() => {
    const cspLink = (
      <VnmTermsLink
        onClick={() => {
          sendToSegment({
            type: 'track_oregon_lmi_link_clicked',
            funnelPacket: context.funnelPacket,
            clicked: 'oregon_csp_website',
          });
        }}
        href="https://oregoncsp.org/li"
      >
        <Text color="secondary" textStyle="paragraph400">
          www.oregoncsp.org/li
        </Text>
      </VnmTermsLink>
    );
    const mailToLink = (
      <VnmTermsLink
        onClick={() => {
          sendToSegment({
            type: 'track_oregon_lmi_link_clicked',
            funnelPacket: context.funnelPacket,
            clicked: 'support_email',
          });
        }}
        href="mailto:solar@communityenergyproject.org"
      >
        <Text color="secondary" textStyle="paragraph400">
          solar@communityenergyproject.org
        </Text>
      </VnmTermsLink>
    );

    return (
      <>
        Eligible low-income customers may qualify for additional savings and can learn more by
        visiting {cspLink} or by contacting the Program. For more information about the Program call
        971 544 8718 or email {mailToLink}.
      </>
    );
  })();

  return {
    title: 'Finally, review your details & make it official.',
    savingBenefitsDisclaimer: '',
    description: `To finalize your enrollment, review your account details${
      hasContracts
        ? ' and check the box below to sign your community solar disclosure form and subscriber agreement'
        : ''
    }. Then you'll be well on your way to all the great benefits of community solar:`,
    submitButton: `${isEnrolledNM || isEnrolledIL ? 'Review and sign forms' : 'Looks good to me!'}`,
    disclaimer: '',
    eligibilityNotice: `${
      isEnrolledNM
        ? 'Once you proceed to sign all required program forms'
        : 'Once you click the button below'
    }, we'll double-check your eligibility and then find you a spot on a community solar farm.`,
    imageType: TERMS_IMAGE as ImageType,
    next: 'Looks good to me!',
    costBenefit: 'No extra cost to you',
    energyBenefit: 'Your power bill will support clean local energy',
    savingsBenefit: `Guaranteed savings of ${
      isEnrolledNM ? 'up to' : ''
    } ${savingsRate} on the solar credits applied to your power bill`,
    paymentNotice,
    lmiNotice,
    accountInfo: `You will receive two bills each month - one from ${utilityName} and one from Arcadia.`,
    bullets: [
      'No extra cost to you',
      'Your power bill will support clean, local energy',
      `Guaranteed savings of ${savingsRate} on the solar credits applied to your power bill`,
    ],
    descriptionFooter:
      'Once you have confirmed the details on this page, we’ll double-check your eligibility and then find you a spot on a local community solar farm.',
    isLmi: {
      primary: 'Yes, I qualify as LMI',
      secondary:
        'If you choose this option, after clicking "Looks good to me" below, you will see a form to self-attest your income status.',
    },
    maybeLmi: {
      primary: "I don't know if I qualify as LMI",
      secondary:
        'If you aren\'t sure, that\'s ok. After clicking "Looks good to me" below, you will see a form to attest your income status. Go ahead and sign it. We will review your income level and check whether you qualify as LMI.',
    },
    notLmi: {
      primary: 'No, I do not qualify as LMI',
      secondary: "You're still guaranteed to save with community solar!",
    },
    lmiSectionTitle: 'Do you qualify as low and moderate income (LMI)?',
    lmiSectionDescription:
      'Households qualify as low and moderate income based on criteria specified by program rules.',
    lmiSavingsPercentageDescription: `*Low-to-moderate income households save ${savingsRate}; all others save 5%.`,
  };
};

export const waitListCopy = () => {
  return {
    title: 'Review your details and kick start your solar journey!',
    savingBenefitsDisclaimer: '',
    description:
      'Click the button below to confirm that all your account details are correct. Then, you’ll be well on your way to enjoying the economic and environmental benefits of community solar:',
    submitButton: 'Looks good to me!',
    disclaimer: '',
    eligibilityNotice:
      'We look forward to letting you know as soon as community solar becomes available in your area!',
    imageType: TERMS_IMAGE_WAITLIST as ImageType,
    next: 'Looks good to me!',
    costBenefit: 'No extra cost to you, ever',
    energyBenefit: 'More clean energy added to your state’s power grid',
    savingsBenefit:
      "You'll receive credits based on the amount of solar energy your share of the farm produces.",
    paymentNotice: '',
    lmiNotice: '',
    accountInfo: '',
    bullets: [
      "You'll receive credits based on the amount of solar energy your share of the farm produces.",
      'More clean energy added to your state’s power grid',
      'No extra cost to you, ever',
    ],
    lmiSectionTitle: '',
    lmiSectionDescription: '',
    lmiSavingsPercentageDescription: '',
  };
};

export const mnlmiCopy = () => {
  return {
    savingsBenefit: 'Guaranteed savings of 10%* on the solar credits applied to your power bill',
    savingBenefitsDisclaimer:
      '*Savings quoted is for qualifying low-to-moderate (LMI) income households. For all others, the savings rate is 5%',
  };
};

export const waitListCopyNoScraper = () => {
  return {
    title: 'Thanks for your interest in Community Solar!',
    savingBenefitsDisclaimer: '',
    description:
      'We look forward to letting you know as soon as Community Solar becomes available in your area. Then, you can enjoy all its economic and environmental benefits:',
    submitButton: 'See my dashboard',
    disclaimer: '',
    eligibilityNotice:
      'We look forward to letting you know as soon as community solar becomes available in your area!',
    imageType: TERMS_IMAGE_WAITLIST as ImageType,
    next: 'Looks good to me!',
    costBenefit: 'No extra cost to you, ever',
    energyBenefit: 'More clean energy added to your state’s power grid',
    savingsBenefit:
      "You'll receive credits based on the amount of solar energy your share of the farm produces.",
    paymentNotice: '',
    lmiNotice: '',
    accountInfo: '',
    bullets: [
      "You'll receive credits based on the amount of solar energy your share of the farm produces.",
      'More clean energy added to your state’s power grid',
      'No extra cost to you, ever',
    ],
    lmiSectionTitle: '',
    lmiSectionDescription: '',
    lmiSavingsPercentageDescription: '',
  };
};

export type VnmTermsCopy = ReturnType<typeof defaultCopy>;

export interface VnmTermsProps {
  context: VnmTermsContext;
  error?: string;
  flags?: {
    showReviewBlock?: boolean;
    showSelfAttestationForm?: boolean;
    showPersonalizedContractPreview?: boolean;
  };
  loading: boolean;
  next: (payload: VnmTermsForm) => void;
  overrideCopy?: (context: VnmTermsContext) => Partial<ReturnType<typeof defaultCopy>>;
  pending: boolean;
}

export interface onSubmitSolarTermsProps {
  context: VnmTermsContext;
  next: (payload: VnmTermsForm) => void;
  lmiStatus: LmiStatus | undefined;
  contractIds: number[];
  selfAttestationData?: SelfAttestationData;
}

export interface VnmTermsContext {
  contracts: ContractVersion[];
  billingSelection?: BillingDataSource;
  businessName?: string;
  email: string;
  funnelPacket: FetchFunnelPacketResponse;
  phoneNumber?: string | null;
  firstName: string;
  lastName: string;
  lmiStatus?: string;
  paymentMethod?: { lastFour: string; source: string };
  promoCode?: string; // used in solar landscape copy
  solarSavingsRate?: string;
  utility: Utility;
  utilityAccountId?: number;
  isUtilityScraperEligible?: boolean;
  forceWaitlist?: boolean;
  funnelId?: string;
}

type LmiStatus = 'isLmi' | 'maybeLmi' | 'notLmi';
const isLmi = 'isLmi';
const maybeLmi = 'maybeLmi';
const notLmi = 'notLmi';
export interface VnmTermsForm {
  lmiStatus?: LmiStatus;
  contractIds: number[];
  selfAttestationData?: SelfAttestationData;
}

export interface SelfAttestationData {
  county: string;
  houseOccupancy: string;
  lmiProgram: string;
  houseIncome: string;
}

export interface FetchUtilityScraperEligibleOptions {}

function shouldConsolidateContracts(
  stateAbbreviation: string,
  isNJAffirmativeConsentEnabled: boolean,
): boolean {
  // Only NJ with enabled flag doesn't require consolidation
  if (stateAbbreviation === 'NJ' && isNJAffirmativeConsentEnabled) {
    return false;
  }
  // All other combinations require consolidation
  return true;
}
